/**
 * Kullanıcının seçtiği/çektiği bir fotoğrafı (File) yüklemeye hazır hale getirir:
 *  1. EXIF orientation'a göre düzeltilmiş bitmap üretir (createImageBitmap).
 *  2. Oranı koruyarak TARGET_MAX_EDGE_PX'e göre downscale eder.
 *  3. JPEG olarak yeniden kodlar — bu adımda EXIF/GPS metadata BİLEREK düşer
 *     (veri minimizasyonu: konum/cihaz bilgisi lead akışına asla girmemeli).
 *
 * `canvas` da döner: imageQuality.ts (netlik/ışık kontrolü) aynı çizimi tekrar
 * decode etmeden kullanabilsin diye — ikinci bir dosya okuma/decode maliyeti yok.
 */
import { TARGET_MAX_EDGE_PX } from "../config";

export interface PreparedImage {
  blob: Blob;
  previewUrl: string;
  width: number;
  height: number;
  canvas: HTMLCanvasElement;
}

export async function prepareImage(file: File): Promise<PreparedImage> {
  const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  const prepared = await prepareFromDrawable(bitmap, bitmap.width, bitmap.height);
  bitmap.close();
  return prepared;
}

/** Bir çizilebilir kaynağı (File kaynağından üretilmiş ImageBitmap ya da canlı
 * video elementi) downscale edip EXIF olmadan JPEG kodlar. prepareImage ve
 * liveCapture AYNI yolu kullanır: tek kodlama davranışı. */
export async function prepareFromDrawable(
  source: CanvasImageSource,
  sourceWidth: number,
  sourceHeight: number,
): Promise<PreparedImage> {
  const longEdge = Math.max(sourceWidth, sourceHeight);
  const scale = longEdge > TARGET_MAX_EDGE_PX ? TARGET_MAX_EDGE_PX / longEdge : 1;
  const width = Math.round(sourceWidth * scale);
  const height = Math.round(sourceHeight * scale);

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2D bağlamı alınamadı");
  ctx.drawImage(source, 0, 0, width, height);

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, "image/jpeg", 0.85),
  );
  if (!blob) throw new Error("Görüntü kodlanamadı");

  return {
    blob,
    previewUrl: URL.createObjectURL(blob),
    width,
    height,
    canvas,
  };
}
